import "./App.css";
import "react-tippy/dist/tippy.css";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import LayoutMobile from "LayoutMobile";
import LayoutDesktop from "LayoutDesktop";
import { MessageDialog, useDialogManager } from "components/dialogs/Dialogs";
import { useZakeke } from "@zakeke/zakeke-configurator-react";
import LoadingOverlay from "./components/widgets/LoadingOverlay";
import useStore from "./Store";

export const Container = styled.div`
  position: relative;
  display: flex;
  flex-flow: column;
  padding: 40px 60px;
  @media (max-width: 1024px) {
    flex-direction: column;
    padding: 0px;
    height: 100%;
  }
  @media (min-width: 1024) {
    width: 100%;
    height: 100%;
  }
`;

export const Top = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: grid;
  grid-template-columns: 1fr 40%;
  min-height: 0;

  @media (max-width: 1024px) {
    display: flex;
    flex-direction: column;
  }
`;

function App() {
  const [resize, setResize] = useState(false);
  const resizeRef = useRef(false);
  resizeRef.current = resize;

  const { showDialog } = useDialogManager();

  const {
    eventMessages,
    product,
    isSceneLoading,
    isAssetsLoading,
    culture,
    currency,
    groups,
    selectOption,
    addFocusAttributesListener,
    isViewerReady,
  } = useZakeke();
  const {
    isLoading,
    setPriceFormatter,
    setSelectedAttributeId,
    setSelectedGroupId,
    setSelectedStepId,
    isMobile,
    selectedGroupId,
    personalDetailsData,
    customSizes,
    initializePersonalDetailsData,
  } = useStore();

  const groupsRef = useRef(groups);
  groupsRef.current = groups;

  // Page resize
  useEffect(() => {
    const resizeFunction = () => {
      setResize(!resizeRef.current);
    };

    window.addEventListener("resize", resizeFunction);
    return () => window.removeEventListener("resize", resizeFunction);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // initialize custom sizes

  useEffect(() => {
    if (groups && groups.length > 1 && personalDetailsData.length === 0) {
      const sizeGroup = groups.find(
        (group) => group.name.toLowerCase() === "size"
      );
      if (sizeGroup) {
        const personalDetailsAttribute =
          sizeGroup.attributes &&
          sizeGroup.attributes.find(
            (attribute) => attribute.name.toLowerCase() === "personal details"
          );
        let initialPersonalDetails: any = [];
        if (personalDetailsAttribute) {
          personalDetailsAttribute.options.forEach((option) => {
            initialPersonalDetails.push({
              label: option.name,
              value: ''
            });
          });
        }
        initializePersonalDetailsData(initialPersonalDetails);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, personalDetailsData]);

  useEffect(() => {
    if (isViewerReady) {
      addFocusAttributesListener((event) => {
        if (event.groups.length > 0) {
          setSelectedGroupId(event.groups[0].groupId);
          const group = groups.find(
            (group) => group.id === event.groups[0].groupId
          );
          if (group && group.steps) {
            const firstStep = group.steps.find((step) =>
              step.attributes.find(
                (attr) => attr.id === event.groups[0].visibleAttributes[0]
              )
            );
            if (firstStep) setSelectedStepId(firstStep.id);
          }
          setSelectedAttributeId(event.groups[0].visibleAttributes[0]);
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewerReady, selectedGroupId]);

  useEffect(() => {
    setPriceFormatter(
      new Intl.NumberFormat(culture, {
        style: "currency",
        currency: currency,
      })
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [culture, currency]);

  useEffect(() => {
    if (product && !isSceneLoading) {
      const welcomeMessage = eventMessages?.find(
        (message) => message.eventID === 3
      );
      if (welcomeMessage && welcomeMessage.visible)
        showDialog(
          "WelcomeMessage",
          <MessageDialog
            alignButtons="center"
            message={welcomeMessage?.description}
          />
        );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, isSceneLoading, eventMessages]);

  useEffect(() => {
    if (product) {
      // Expose the select option for algho
      (window as any).zakekeSelectOption = handleAlghoSelectOption;
      (window as any).zakekeSelectAttribute = handleAlghoSelectAttribute;
      (window as any).zakekeSelectGroup = handleAlghoSelectGroup;

      if (product.id !== 28547 && product.id !== 28546) return;

      console.log("Adding Algho script...");

      const botShoe = "8e8ce95f27b6e39c5f622bc2f141b20f ";
      const botCouch = "31310bfe57aa266476a8cdc1921d26da";

      const questionIdCouch = "284256";
      const questionIdShoe = "284301";

      const tag = document.createElement("algho-viewer");
      tag.setAttribute("bot-id", product.id === 28547 ? botShoe : botCouch);
      tag.setAttribute("auto-hide-message", "0");
      tag.setAttribute("widget", "true");
      tag.setAttribute("audio", "true");
      tag.setAttribute("voice", "true");
      tag.setAttribute("dhi", "true");
      tag.setAttribute("floating", "true");
      tag.setAttribute("transparent", "true");
      tag.setAttribute("allow-fullscreen", "false");
      tag.setAttribute("open", "false");
      tag.setAttribute("z-index", "0");
      tag.setAttribute("question-repeat", "true");
      tag.setAttribute(
        "question-id",
        product.id === 28547 ? questionIdShoe : questionIdCouch
      );

      tag.setAttribute("widget-left", "40px");
      tag.setAttribute("floating-left", "true");

      document.body.appendChild(tag);

      const script = document.createElement("script");
      script.setAttribute("id", "algho-viewer-module");
      script.setAttribute("type", "text/javascript");
      script.setAttribute("defer", "defer");
      script.setAttribute(
        "src",
        "https://test-fe.alghoncloud.com/algho-viewer.min.js"
      );
      document.body.appendChild(script);
    }

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const { setIsMobile } = useStore();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Called from Algho bot to select an option
   * @param optionId The option to focus and select
   */
  const handleAlghoSelectOption = (optionId: number) => {
    console.log("Algho triggered select option", optionId);

    // Get attribute id for that option
    let attributeId: number | null = null;
    let groupId: number | null = null;
    let stepId: number | null = null;

    groupsRef.current
      .flatMap((g) => g.attributes)
      .concat(
        groupsRef.current.flatMap((g) => g.steps.flatMap((s) => s.attributes))
      )
      .forEach((attr) => {
        let _option = attr.options.find((option) => option.id === optionId);
        if (_option) {
          attributeId = attr.id;
          groupId = attr.groupId;
          stepId = attr.stepId;
        }
      });

    if (attributeId) {
      setSelectedGroupId(groupId);
      setSelectedStepId(stepId);
      setSelectedAttributeId(attributeId);
      selectOption(optionId);
    }
  };

  /**
   * Called from Algho bot to select an attribute
   * @param attributeId The option to focus and select
   */
  const handleAlghoSelectAttribute = (attributeId: number) => {
    console.log("Algho triggered select attribute", attributeId);

    const attribute = groupsRef.current
      .flatMap((g) => g.attributes)
      .concat(
        groupsRef.current.flatMap((g) => g.steps.flatMap((s) => s.attributes))
      )
      .find((x) => x.id === attributeId);

    if (!attribute) return;

    const { stepId, groupId } = attribute;

    setSelectedGroupId(groupId);
    setSelectedStepId(stepId);
    setSelectedAttributeId(attributeId);
  };

  /**
   * Called from Algho bot to select an attribute
   * @param attributeId The option to focus and select
   */
  const handleAlghoSelectGroup = (groupId: number) => {
    console.log("Algho triggered select group", groupId);

    setSelectedGroupId(groupId);
  };

  return (
    <>
      {isMobile ? <LayoutMobile /> : <LayoutDesktop />}
      {(isLoading || isSceneLoading || isAssetsLoading) && <LoadingOverlay />}
    </>
  );
}

export default App;
